import React, { useEffect, useRef, useState } from 'react';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import userHelper from '../../../Helpers/userHelper';
import ApolloIgnoredClientService from '../../../Services/ApolloIgnoredClientService';
import OfficeService from '../../../Services/OfficeService';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import OfficeGeneralEditPanelDescription from './OfficeGeneralEditPanelDescription';
import ApolloIgnoredClientEditWindowDescription from './ApolloIgnoredClientEditWindowDescription';
import ApolloIgnoredClientEditItem from '../../../Domain/ApolloIgnoredClientEditItem';
import OgSectionHead from './components/OgSectionHead';
import OgField from './components/OgField';
import OgToggleField from './components/OgToggleField';
import OgRate from './components/OgRate';
import OgManagerCard from './components/OgManagerCard';
import OgNameChips from './components/OgNameChips';
import OgIgnoredClients from './components/OgIgnoredClients';

const MANAGERS = [
  { role: 'Global Business Development Manager', field: 'globalBusinessDevelopmentManager' },
  { role: 'Global Business Intelligence Manager', field: 'globalBusinessIntelligenceManager' },
  { role: 'Global Finance Manager', field: 'globalFinanceManager' },
  { role: 'Global Human Resources Manager', field: 'globalHumanResourcesManager' },
  { role: 'Global Information Technology Manager', field: 'globalInformationTechnologyManager' },
  { role: 'Global Marketing Manager', field: 'globalMarketingManager' },
  { role: 'Global Operations Manager', field: 'globalOperationsManager' },
  { role: 'Global Resource Management Manager', field: 'globalResourceManagementManager' },
  { role: 'Global Data Quality Manager', field: 'globalDataQualityManager' },
];

function PricingSection({ eyebrow, title, sub, items, unit }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      <OgSectionHead eyebrow={eyebrow} title={title} sub={sub} />
      <div className="og-grid c3">
        {items.map((it, i) => (
          <OgRate key={i} name={it.text} value={it.misc} unit={unit} />
        ))}
      </div>
    </>
  );
}

function OfficeGeneralAdmin() {
  const [item, setItem] = useState(null);
  const [ignoredIds, setIgnoredIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [ignoredModal, setIgnoredModal] = useState(false);
  const prevFinalizationRef = useRef(null);

  const canManage = userHelper.doesUserHaveRole(['Admin', 'OfficeAdminWrite', 'OfficeGeneralAdmin']);
  const canEditClients = userHelper.doesUserHaveRole(['Admin']);

  function populate() {
    setLoading(true);
    const prev = prevFinalizationRef.current;
    OfficeService.GetOfficeGlobalData()
      .then((result) => {
        result.yrmoCutoffDate = dateTimeHelper.convertUTCDateStringToLocale(result.yrmoCutoffDate).toString();
        if (prev && !result.allowInvoiceFinalization) {
          ErrorMessage.SetApprove(
            "You set 'Allow Invoice Finalization' to false, would you like to send out a Missed Invoices Notification Email?",
            missedInvoiceEmail
          );
        }
        prevFinalizationRef.current = result.allowInvoiceFinalization;
        setItem(result);
        return ApolloIgnoredClientService.GetApolloIgnoredClientList();
      })
      .then((list) => {
        if (list) setIgnoredIds(list.map((x) => x.clientId));
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        ErrorMessage.SetErrorMessage(`Error! ${err ? err.errorMessage : ''}`);
      });
  }

  useEffect(() => {
    populate();
  }, []);

  function missedInvoiceEmail() {
    OfficeService.MissedInvoicesEmailNotification();
    ErrorMessage.SetNotification('Email request submitted, you will get a message about the results.');
  }

  function onMissedInvoiceClick() {
    ErrorMessage.SetApprove('Are you sure you want to send the Missed Invoices Notification email?', missedInvoiceEmail);
  }

  function onSaveEdit() {
    setEditing(false);
    populate();
  }

  function onCloseIgnoredModal() {
    setIgnoredModal(false);
    populate();
  }

  if (loading) {
    return (
      <div className="contentwrapper bravo-ds">
        <div className="og-scroll">
          <div className="og-wrap">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contentwrapper bravo-ds">
      <div className="og-scroll">
        <div className="og-wrap">
          {canManage ? (
            <div className="og-toolbar actions">
              <button type="button" className="btn" onClick={onMissedInvoiceClick}>
                <i className="fa-solid fa-envelope" /> Missed Inv. Email
              </button>
              <div className="divider" />
              <button type="button" className="btn primary" onClick={() => setEditing(true)}>
                <i className="fa-solid fa-pen" /> Edit
              </button>
            </div>
          ) : null}

          <OgSectionHead eyebrow="Global Settings" title="Default Cost Sources" />
          <div className="og-grid c2">
            <OgField label="Default Mystery Shopping Platform Pricing" value={item.defaultMysteryShoppingPlatformPricingName} />
            <OgField label="Default Call Recording Cost" value={item.defaultCallRecordingCostName} />
            <OgField label="Default IWR / CSAT Cost" value={item.defaultCsatcostName} />
            <OgField label="Default IVR Cost" value={item.defaultIvrcostName} muted />
          </div>

          <OgSectionHead eyebrow="Global Settings" title="End of Month & Invoicing" />
          <div className="og-grid c4">
            <OgField label="EOM Cutoff Day" req num value={item.endOfMonthCutoffDay} />
            <OgField label="DDP Reject Surcharge" req num value={item.ddprejectSurcharge} />
            <OgField label="Current YRMO" num value={item.currentYRMO} />
            <OgField label="Default Invoice Late Fee %" num value={item.defaultInvoiceInterestRate} />
            <OgField label="Late Fee Grace Period In Days" req num value={item.lateFeeGracePeriodInDays} />
            <OgField label="YRMO Cutoff Date" small span value={item.yrmoCutoffDate} />
            <OgToggleField label="Allow Invoice Finalization" on={item.allowInvoiceFinalization} />
            <OgToggleField label="Allow End of Month Automation" on={item.allowEndOfMonthAutomation} />
          </div>

          <OgSectionHead eyebrow="Global Settings" title="Opportunities, Projects & Users" />
          <div className="og-grid c4">
            <OgField label="Maximum Opportunity Duration (months)" req num value={item.maximumOpportunityDuration} />
            <OgField
              label="Max Project End vs. Opportunity End (months)"
              req
              num
              value={item.maximumIntervalInMonthsBetweenOpportunityEndDateAndProjectEndDate}
            />
            <OgField label="Evaluator Statement Delay (hours)" req num value={item.evaluatorStatementDelay} />
            <OgField label="Global User Deactivation Days Limit" num value={item.globalUserDeactivationDaysLimit} />
          </div>

          <OgSectionHead eyebrow="Global Settings" title="Approvals & Notifications" />
          <div className="og-grid c2">
            <div className="og-field">
              <div className="lbl">Users that require CEO Approval for Commission</div>
              <OgNameChips
                names={(item.commissionRequiresCeoApprovalUserNames || '')
                  .split(',')
                  .map((n) => n.trim())
                  .filter(Boolean)}
              />
            </div>
            <OgField label="Group that receives EOM Period Emails" value={item.eomPeriodEmailRecipientGroupName} />
          </div>

          <PricingSection
            eyebrow="Pricing"
            title="Mystery Shopping System"
            sub="Cost / evaluation · USD"
            items={item.mysteryShoppingPlatformPricings}
            unit="per evaluation"
          />
          <PricingSection
            eyebrow="Pricing"
            title="Call Recording"
            sub="Per minute"
            items={item.callRecordingCosts}
            unit="per minute"
          />
          <PricingSection
            eyebrow="Pricing"
            title="IWR / CSAT"
            sub="Location / month"
            items={item.csatcosts}
            unit="per location / month"
          />
          <PricingSection
            eyebrow="Pricing"
            title="IVR"
            sub="Cost / evaluation"
            items={item.ivrcosts}
            unit="per evaluation"
          />

          <OgSectionHead eyebrow="People" title="Global Managers" />
          <div className="og-grid c3">
            {MANAGERS.map((m, i) => (
              <OgManagerCard key={m.field} role={m.role} userId={item[m.field]} colorIndex={i} />
            ))}
          </div>

          <OgSectionHead eyebrow="Exclusions" title="Ignored Clients" />
          <OgIgnoredClients
            ids={ignoredIds}
            canEdit={canEditClients}
            onEdit={() => setIgnoredModal(true)}
          />
        </div>
      </div>

      {editing ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Update}
          editedItem={item}
          clickOnCancel={() => setEditing(false)}
          clickOnSave={onSaveEdit}
          windowDescription={OfficeGeneralEditPanelDescription}
          errorCode="OGA266"
        />
      ) : null}

      {ignoredModal ? (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new ApolloIgnoredClientEditItem()}
          clickOnCancel={() => setIgnoredModal(false)}
          clickOnSave={onCloseIgnoredModal}
          windowDescription={ApolloIgnoredClientEditWindowDescription}
          approveBeforeSaveCheck={() => true}
          approveBeforeSaveMessage="Are you sure you want to update the Apollo Ignored Clients list?"
          errorCode="AIC"
        />
      ) : null}
    </div>
  );
}

export default OfficeGeneralAdmin;
